import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import cn from 'classnames';
import { List, useListRef, RowComponentProps } from 'react-window';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import IconArrowUp from '@creditflowresearch/uilib/Icon/IconArrowUp';
import Button from '@creditflowresearch/uilib/Button';
import { isScreenXs } from '@creditflowresearch/uilib/utils/screen';
import CatalogNavigation from './CatalogNavigation';
import CatalogItem from './CatalogItem';
import { ICatalogItemData } from './CatalogItem/CatalogItem';
import ICatalogCollection from '@/creditflowresearch/application/Catalog/common';
import {
  VirtualListContextProvider, useVirtualListContext,
} from '@creditflowresearch/uilib/VirtualListContext';
import { OFFSET } from './constants';

import styles from './catalogList.module.scss';

interface IProps {
  isFetching: boolean,
  data: ICatalogCollection,
  catalogUrl: string,
  bodyHeight?: number,
}

const OVERSCAN_COUNT = 2;

// Items are rendered by ourselves in the document flow; react-window only computes the visible range
// from the window scroll position mirrored into this hidden list.
const NullRow: (props: RowComponentProps) => null = () => null;
const emptyRowProps = {};
const rangeEngineStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  overflow: 'hidden',
  visibility: 'hidden',
  pointerEvents: 'none',
};

const getViewportHeight = (fallback: number): number => (
  typeof window === 'undefined' ? fallback : window.innerHeight
);

interface IListProps extends Omit<IProps, 'data'> {
  listData: ICatalogItemData[],
  labels: string[],
}

const CatalogList = ({
  isFetching,
  listData,
  labels,
  catalogUrl,
  bodyHeight = 600,
}: IListProps) => {
  const listRef = useListRef(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const isMobile = isScreenXs();
  const {
    getSize,
    getOffset,
    totalHeight,
    range,
    handleRowsRendered,
  } = useVirtualListContext();

  const hasResults = !!labels.length;

  // Mirror the window scroll position into the hidden react-window List so it recomputes the visible range.
  useEffect(() => {
    if (isFetching || !hasResults) {
      return undefined;
    }

    const handleScroll = () => {
      const engine = listRef.current?.element;
      const items = itemsRef.current;

      if (!engine || !items) {
        return;
      }

      engine.scrollTop = Math.max(0, -items.getBoundingClientRect().top);
    };

    handleScroll();
    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [isFetching, hasResults, listRef]);

  const onNavigationClickHandler = useCallback((index: number) => {
    const items = itemsRef.current;

    if (items) {
      window.scrollTo({
        top: items.getBoundingClientRect().top + window.scrollY + getOffset(index),
      });
    }

    return false;
  }, [getOffset]);

  const onScrollUp = () => window.scrollTo(0, 1);

  const renderedItems = useMemo((): React.ReactNode[] => {
    const stopIndex = Math.min(range.stopIndex, listData.length - 1);
    const result: React.ReactNode[] = [];

    for (let index = range.startIndex; index <= stopIndex; index++) {
      result.push(
        <CatalogItem
          key={ index }
          index={ index }
          items={ listData }
          catalogUrl={ catalogUrl }
        />,
      );
    }

    return result;
  }, [range, listData, catalogUrl]);

  if (isFetching) {
    return (
      <div className={ cn(styles.container, { [styles.emptyContainer]: isFetching }) }>
        <Spinner />
      </div>
    );
  }

  if (!hasResults) {
    return null;
  }

  return (
    <>
      <CatalogNavigation
        onClickHandler={ onNavigationClickHandler }
        navigationFilter={ labels }
      />
      <div className={ styles.container }>
        <div
          className={ styles.outerElement }
          style={ { paddingTop: isMobile ? 0 : OFFSET } }
        >
          <List
            aria-hidden
            listRef={ listRef }
            style={ { ...rangeEngineStyle, height: getViewportHeight(bodyHeight) } }
            rowCount={ listData.length }
            rowHeight={ getSize }
            overscanCount={ OVERSCAN_COUNT }
            rowComponent={ NullRow }
            rowProps={ emptyRowProps }
            onRowsRendered={ handleRowsRendered }
          >
            <div style={ { height: totalHeight } } />
          </List>
          <div
            ref={ itemsRef }
            style={ { position: 'relative', height: totalHeight } }
          >
            <div style={ { position: 'absolute', top: getOffset(range.startIndex), width: '100%' } }>
              { renderedItems }
            </div>
          </div>
        </div>
        <Button
          dataTest='catalogScrollUpButton'
          onClick={ onScrollUp }
          variant="outline"
          className={ styles.buttonScrollUp }
          icon={ <IconArrowUp className={ styles.icon } /> }
          ariaLabel="Scroll to top"
        />
      </div>
    </>
  );
};

export default ({ data, ...props }: IProps) => {
  const listData = useMemo(
    () => Object.entries(data).map(([key, value]) => ({ [key]: value })),
    [data],
  );
  const labels = useMemo(() => Object.keys(data), [data]);

  return (
    <VirtualListContextProvider { ...props } data={ listData }>
      <CatalogList { ...props } listData={ listData } labels={ labels } />
    </VirtualListContextProvider>
  );
};
